/**
 * Sessão do operador — fica no aparelho para a portaria não precisar relogar a
 * cada abertura, e porque o app tem que subir mesmo sem rede.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import {definirToken} from './api/cliente';
import {ticketeira} from './config';
import {limparCatalogo} from './dados/repositorio';

const CHAVE_SESSAO = 'tmobi.validador.sessao';
const CHAVE_APARELHO = 'tmobi.validador.aparelho';

export interface SessaoSalva {
  usuarioId: string;
  nome: string;
  token: string;
  machineId: number;
  ticketeira: string;
  autenticadoEm: string;
  /**
   * Sessão da conta de demonstração: os dados são de exemplo, do próprio
   * aparelho, e nada deste turno vai para o servidor.
   */
  demonstracao?: boolean;
}

/**
 * Identificador do aparelho. O servidor devolve o `device_id` definitivo no
 * login; este id só precisa ser estável entre aberturas do app.
 */
export async function idDoAparelho(): Promise<string> {
  const salvo = await AsyncStorage.getItem(CHAVE_APARELHO);
  if (salvo) {
    return salvo;
  }
  const novo =
    'rn-' +
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10);
  await AsyncStorage.setItem(CHAVE_APARELHO, novo);
  return novo;
}

export async function salvarSessao(sessao: SessaoSalva): Promise<void> {
  await AsyncStorage.setItem(CHAVE_SESSAO, JSON.stringify(sessao));
  definirToken(sessao.token);
}

/**
 * Recupera a sessão salva. Sessão de outra ticketeira é descartada — o mesmo
 * aparelho pode ter tido outro APK instalado antes.
 */
export async function carregarSessao(): Promise<SessaoSalva | null> {
  const bruto = await AsyncStorage.getItem(CHAVE_SESSAO);
  if (!bruto) {
    return null;
  }
  try {
    const sessao = JSON.parse(bruto) as SessaoSalva;
    if (sessao.ticketeira !== ticketeira().id) {
      await encerrarSessao();
      return null;
    }
    definirToken(sessao.token);
    return sessao;
  } catch {
    return null;
  }
}

/**
 * Fecha a sessão e apaga o catálogo baixado.
 *
 * A limpeza vive aqui, e não na tela, para valer em todo caminho de saída —
 * inclusive no descarte de sessão de outra ticketeira feito por
 * `carregarSessao`. É a regra do TMobi original (`PanelActivity.logout`):
 * o operador seguinte baixa os eventos dele em vez de herdar os do anterior.
 *
 * `limparCatalogo` preserva as validações; o que ela apaga é só o que o
 * servidor sabe devolver. Ver o comentário dela em src/dados/repositorio.ts.
 *
 * Depende do banco já preparado — o que é verdade: App.tsx chama
 * `prepararBanco()` antes de qualquer `carregarSessao()`.
 */
export async function encerrarSessao(): Promise<void> {
  await AsyncStorage.removeItem(CHAVE_SESSAO);
  definirToken('');
  await limparCatalogo();
}
